import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  InfoBox,
  Modal,
  notify,
  Progress,
  Stack,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useRef, useState } from "react";
import {
  MdAdd,
  MdClose,
  MdCloudUpload,
  MdInsertDriveFile,
} from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";

interface DocumentUploaderProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  defaultFolderId?: string;
}

const ALLOWED_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  "application/json",
  "application/jsonl",
  "application/x-ndjson",
  "application/octet-stream",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

const ALLOWED_EXTENSIONS = [".pdf", ".txt", ".md", ".docx", ".doc", ".jsonl"];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

type FileItemStatus = "pending" | "uploading" | "confirming" | "done" | "error";

interface UploadQueueItem {
  localId: string;
  file: File;
  status: FileItemStatus;
  progress: number;
  error?: string;
}

const STATUS_BADGE_COLOR: Record<FileItemStatus, string> = {
  pending: "gray",
  uploading: "blue",
  confirming: "yellow",
  done: "green",
  error: "red",
};

const uploadWithConcurrency = async (
  items: UploadQueueItem[],
  uploadFn: (item: UploadQueueItem) => Promise<void>,
  concurrency = 3,
): Promise<void> => {
  const queue = [...items];
  const startNext = (): Promise<void> => {
    if (queue.length === 0) return Promise.resolve();
    const item = queue.shift()!;
    return uploadFn(item).finally(startNext);
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, startNext),
  );
};

interface QueueRowProps {
  item: UploadQueueItem;
  onRemove: (localId: string) => void;
  isUploading: boolean;
}

const QueueRow = ({ item, onRemove, isUploading }: QueueRowProps) => {
  const { t } = useTranslation();

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Stack
      gap={1}
      p={2}
      borderRadius="md"
      bg={{ base: "gray.50", _dark: "gray.800" }}
    >
      <HStack justify="space-between" align="center">
        <HStack gap={2} flex={1} minW={0}>
          <MdInsertDriveFile
            size={20}
            color="#718096"
            style={{ flexShrink: 0 }}
          />
          <Stack gap={0} minW={0} flex={1}>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {item.file.name}
            </Text>
            <Text fontSize="xs" color={{ base: "gray.500", _dark: "gray.400" }}>
              {formatFileSize(item.file.size)}
            </Text>
          </Stack>
        </HStack>
        <HStack gap={2} flexShrink={0}>
          <Badge colorPalette={STATUS_BADGE_COLOR[item.status]} size="sm">
            {t(`app.knowledgeBase.upload.status.${item.status}`)}
          </Badge>
          {item.status === "pending" && (
            <IconButton
              size="xs"
              aria-label={t("app.knowledgeBase.upload.removeFile")}
              variant="ghost"
              colorPalette="gray"
              onClick={() => onRemove(item.localId)}
              disabled={isUploading}
            >
              <MdClose />
            </IconButton>
          )}
        </HStack>
      </HStack>

      {item.status === "uploading" && (
        <Progress value={item.progress} size="xs" colorPalette="blue" />
      )}

      {item.status === "error" && item.error && (
        <InfoBox variant="error">
          <Text fontSize="xs">{item.error}</Text>
        </InfoBox>
      )}
    </Stack>
  );
};

const DocumentUploader = ({
  isOpen,
  onClose,
  onUploadComplete,
  defaultFolderId,
}: DocumentUploaderProps) => {
  const { t } = useTranslation();
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const uploadSuccessCount = useRef(0);

  const resetState = useCallback(() => {
    setQueue([]);
    setIsUploading(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    resetState();
    onClose();
  }, [isUploading, onClose, resetState]);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return t("app.knowledgeBase.upload.invalidType");
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return t("app.knowledgeBase.upload.fileTooLarge");
    }
    return null;
  };

  const addFilesToQueue = useCallback((files: File[]) => {
    const newItems: UploadQueueItem[] = files.map((file) => {
      const validationError = validateFile(file);
      return {
        localId: `${file.name}-${file.size}-${Math.random()}`,
        file,
        status: validationError ? "error" : "pending",
        progress: 0,
        error: validationError ?? undefined,
      };
    });
    setQueue((prev) => [...prev, ...newItems]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFromQueue = useCallback((localId: string) => {
    setQueue((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) addFilesToQueue(droppedFiles);
    },
    [addFilesToQueue],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    if (selectedFiles.length > 0) addFilesToQueue(selectedFiles);
    e.target.value = "";
  };

  const uploadSingle = async (item: UploadQueueItem): Promise<void> => {
    const updateItem = (patch: Partial<UploadQueueItem>) =>
      setQueue((prev) =>
        prev.map((q) => (q.localId === item.localId ? { ...q, ...patch } : q)),
      );

    try {
      updateItem({ status: "uploading", progress: 0 });

      const contentType = item.file.type || "application/octet-stream";

      const response = await knowledgeBaseService.getUploadUrl({
        fileName: item.file.name,
        contentType,
      });

      await knowledgeBaseService.uploadFileToS3(
        response.presignedUrl,
        item.file,
        contentType,
        (percent) => updateItem({ progress: Math.round(percent * 0.8) }),
      );

      updateItem({ status: "confirming", progress: 90 });

      await knowledgeBaseService.confirmUpload({
        docId: response.docId,
        fileName: item.file.name,
        contentType,
        s3Key: response.s3Key,
        folderId: defaultFolderId,
      });

      updateItem({ status: "done", progress: 100 });
      uploadSuccessCount.current++;
    } catch (err: any) {
      updateItem({
        status: "error",
        error:
          err.response?.data?.message ||
          err.message ||
          t("app.knowledgeBase.upload.failed"),
      });
    }
  };

  const handleUploadAll = async () => {
    const pendingItems = queue.filter((item) => item.status === "pending");
    if (pendingItems.length === 0) return;

    setIsUploading(true);
    try {
      await uploadWithConcurrency(pendingItems, uploadSingle);
    } finally {
      setIsUploading(false);
      const done = uploadSuccessCount.current;
      uploadSuccessCount.current = 0;
      const total = pendingItems.length;
      if (done > 0) onUploadComplete();
      if (done === total) {
        resetState();
        onClose();
      } else {
        notify({
          type: done > 0 ? "warning" : "error",
          title: t("app.knowledgeBase.upload.summary", {
            success: done,
            total,
          }),
        });
      }
    }
  };

  if (!isOpen) return null;

  const pendingCount = queue.filter((q) => q.status === "pending").length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <Heading size="lg" color={{ base: "navy.700", _dark: "white" }}>
            {t("app.knowledgeBase.upload.title")}
          </Heading>
          <Modal.CloseTrigger asChild>
            <Button intent="ghost" size="sm" p={0} disabled={isUploading}>
              <MdClose className="h-6 w-6" />
            </Button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>

      <Modal.Body>
        <VStack gap={4}>
          {/* Dropzone */}
          <Box
            w="full"
            p={5}
            border="2px dashed"
            borderColor={
              isDragging ? "blue.500" : { base: "gray.300", _dark: "gray.600" }
            }
            borderRadius="lg"
            bg={
              isDragging
                ? { base: "blue.50", _dark: "blue.900" }
                : { base: "gray.50", _dark: "gray.800" }
            }
            transition="all 0.2s"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            cursor="pointer"
            onClick={() => document.getElementById("kb-file-input")?.click()}
            _hover={{
              borderColor: "blue.400",
              bg: { base: "gray.100", _dark: "gray.700" },
            }}
          >
            <input
              id="kb-file-input"
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.join(",")}
              onChange={handleInputChange}
              style={{ display: "none" }}
            />
            <VStack gap={2}>
              <MdCloudUpload
                size={36}
                color={isDragging ? "#3182CE" : "#A0AEC0"}
              />
              <Text
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.knowledgeBase.upload.dropzone")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.knowledgeBase.upload.supportedFormats")}
              </Text>
              <Text
                fontSize="xs"
                color={{ base: "gray.500", _dark: "gray.500" }}
              >
                {t("app.knowledgeBase.upload.maxSize")}
              </Text>
            </VStack>
          </Box>

          {/* Queue header */}
          {queue.length > 0 && (
            <HStack justify="space-between" w="full">
              <Text
                fontSize="sm"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.knowledgeBase.upload.filesSelected", {
                  count: queue.length,
                })}
              </Text>
              <Button
                intent="ghost"
                size="sm"
                onClick={() =>
                  document.getElementById("kb-file-input")?.click()
                }
                disabled={isUploading}
              >
                <MdAdd />
                {t("app.knowledgeBase.upload.addMoreFiles")}
              </Button>
            </HStack>
          )}

          {/* Queue list */}
          {queue.length > 0 && (
            <Stack gap={2} w="full" maxH="320px" overflowY="auto">
              {queue.map((item) => (
                <QueueRow
                  key={item.localId}
                  item={item}
                  onRemove={removeFromQueue}
                  isUploading={isUploading}
                />
              ))}
            </Stack>
          )}
        </VStack>
      </Modal.Body>

      <Modal.Footer>
        <HStack gap={3} w="full">
          <Button
            intent="secondary"
            onClick={handleClose}
            disabled={isUploading}
            flex={1}
          >
            {t("app.common.cancel")}
          </Button>
          <Button
            intent="primary"
            onClick={handleUploadAll}
            disabled={pendingCount === 0 || isUploading}
            loading={isUploading}
            flex={1}
          >
            {t("app.knowledgeBase.upload.uploadAll")}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentUploader;
